/**
 * Algolia Mobile Search — Full-screen overlay
 * Activates on mobile only (<750px). When the mobile search input receives
 * focus, a full-screen overlay slides down with an auto-focused search input,
 * query suggestions, and product results.
 *
 * Uses the raw algoliasearch client directly (no autocomplete-js library).
 * Price/VAT helpers are duplicated from algolia-autocomplete.js (IIFE-scoped).
 */
(function() {
  'use strict';

  /* Mobile + tablet (<1024px) — desktop uses algolia-autocomplete.js */
  if (window.innerWidth >= 1024) return;

  /* ── Helpers (duplicated from algolia-autocomplete.js — IIFE-scoped) ── */

  function vatMode() {
    try { return localStorage.getItem('vatPreference') || 'inc'; } catch(e) { return 'inc'; }
  }

  function fmtPrice(p) {
    if (p == null) return '';
    var n = parseFloat(p);
    if (isNaN(n)) return '';
    return '\u00A3' + (vatMode() === 'ex' ? n / 1.2 : n).toFixed(2);
  }

  function vatLabel() { return vatMode() === 'ex' ? 'EX VAT' : 'INC VAT'; }

  function getCheapestPrice(hit) {
    if (window.__b2bTier && hit.meta && hit.meta.custom && hit.meta.custom.b2b_prices) {
      var b2bPrices = hit.meta.custom.b2b_prices;
      var cheapest = Infinity;
      for (var vid in b2bPrices) {
        if (b2bPrices.hasOwnProperty(vid) && b2bPrices[vid][window.__b2bTier] !== undefined) {
          var vp = b2bPrices[vid][window.__b2bTier];
          if (vp < cheapest) cheapest = vp;
        }
      }
      return (cheapest < Infinity) ? cheapest : (hit.variants_min_price || hit.price);
    }
    return hit.variants_min_price || hit.price;
  }

  /* HTML escaping for text content and attribute values */
  function esc(s) {
    var d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
  }

  function escAttr(s) {
    if (s == null) return '';
    return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function debounce(fn, ms) {
    var t;
    return function() {
      var a = arguments, c = this;
      clearTimeout(t);
      t = setTimeout(function() { fn.apply(c, a); }, ms);
    };
  }

  /* ── Wait for Algolia dependencies ── */

  function waitForDeps(cb) {
    var attempts = 0;
    (function check() {
      if (typeof algoliasearch !== 'undefined' && window.SlAlgoliaConfig) return cb();
      if (++attempts > 200) return;
      setTimeout(check, 50);
    })();
  }

  /* ── Build overlay DOM ── */

  function createOverlay() {
    var overlay = document.createElement('div');
    overlay.className = 'sl-mobile-search-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Search products');
    overlay.innerHTML =
      '<div class="sl-mobile-search-header">' +
        '<div class="sl-mobile-search-input-wrap">' +
          '<input type="search" class="sl-mobile-search-input" placeholder="Search for products..." ' +
            'autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false" ' +
            'aria-label="Search for products">' +
          '<button type="button" class="sl-mobile-search-clear" aria-label="Clear search" hidden>' +
            '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
          '</button>' +
        '</div>' +
        '<button type="button" class="sl-mobile-search-cancel" aria-label="Close search">Cancel</button>' +
      '</div>' +
      '<div class="sl-mobile-search-results"></div>';

    document.body.appendChild(overlay);
    return overlay;
  }

  /* ── Main initialisation ── */

  function init() {
    var config = window.SlAlgoliaConfig;
    var client = algoliasearch(config.appId, config.apiKey);
    var productsIndex = config.indexName;
    var suggestionsIndex = config.suggestionsIndex || 'shopify_products_query_suggestions';

    /* Find mobile search input */
    var mobileForm = document.querySelector('.header-search-inline--mobile');
    if (!mobileForm) return;
    var mobileInput = mobileForm.querySelector('.header-search-inline__input');
    if (!mobileInput) return;

    /* Create overlay (hidden initially) */
    var overlay = createOverlay();
    var searchInput = overlay.querySelector('.sl-mobile-search-input');
    var clearBtn = overlay.querySelector('.sl-mobile-search-clear');
    var closeBtn = overlay.querySelector('.sl-mobile-search-cancel');
    var resultsContainer = overlay.querySelector('.sl-mobile-search-results');

    var isOpen = false;
    var lastQueryID = '';
    var lastProductsTotal = 0;
    var triggerElement = null;

    /* Screen reader live region for result announcements */
    var srAnnounce = document.createElement('div');
    srAnnounce.setAttribute('role', 'status');
    srAnnounce.setAttribute('aria-live', 'polite');
    srAnnounce.setAttribute('aria-atomic', 'true');
    srAnnounce.className = 'visually-hidden';
    overlay.appendChild(srAnnounce);

    /* Focus trapping — Tab cycles within the overlay only */
    overlay.addEventListener('keydown', function(e) {
      if (e.key !== 'Tab') return;
      var focusable = overlay.querySelectorAll(
        'input, button:not([hidden]), a[href], [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });

    /* ── Open / Close ── */

    function openOverlay() {
      if (isOpen) return;
      isOpen = true;
      triggerElement = document.activeElement;
      overlay.classList.add('sl-mobile-search-overlay--open');
      /* Hide page content from screen readers */
      var main = document.querySelector('main') || document.getElementById('MainContent');
      if (main) main.setAttribute('aria-hidden', 'true');
      /* iOS scroll lock: position:fixed + save scroll position */
      var scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + scrollY + 'px';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      /* Small delay to let the transition start before focusing */
      setTimeout(function() {
        searchInput.focus();
      }, 50);
    }

    function closeOverlay() {
      if (!isOpen) return;
      isOpen = false;
      overlay.classList.remove('sl-mobile-search-overlay--open');
      /* Restore page content to screen readers */
      var main = document.querySelector('main') || document.getElementById('MainContent');
      if (main) main.removeAttribute('aria-hidden');
      /* Restore scroll position */
      var scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
      searchInput.value = '';
      clearBtn.hidden = true;
      resultsContainer.innerHTML = '';
      srAnnounce.textContent = '';
      lastProductsTotal = 0;
      /* Return focus to trigger element */
      if (triggerElement) {
        triggerElement.focus();
        triggerElement = null;
      }
    }

    /* ── Event listeners ── */

    /* Open overlay when mobile input is focused */
    mobileInput.addEventListener('focus', function(e) {
      e.target.blur(); /* Prevent keyboard on the original input */
      openOverlay();
    });

    /* Also open on tap (in case focus doesn't fire) */
    mobileInput.addEventListener('click', function(e) {
      e.preventDefault();
      openOverlay();
    });

    closeBtn.addEventListener('click', closeOverlay);

    /* Escape key closes overlay */
    overlay.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') closeOverlay();
    });

    /* Clear button */
    clearBtn.addEventListener('click', function() {
      searchInput.value = '';
      clearBtn.hidden = true;
      resultsContainer.innerHTML = '';
      lastProductsTotal = 0;
      searchInput.focus();
    });

    /* Submit on Enter */
    searchInput.addEventListener('keydown', function(e) {
      if (e.key === 'Enter') {
        var q = searchInput.value.trim();
        if (q) {
          window.location.href = '/search?q=' + encodeURIComponent(q) + '&type=product';
        }
      }
    });

    /* Track Insights clicks (delegated) */
    resultsContainer.addEventListener('click', function(e) {
      var link = e.target.closest('.sl-mobile-product');
      if (!link) return;
      try {
        if (window.SlAlgoliaEvents) {
          var oid = link.dataset.objectId;
          var qid = link.dataset.queryId;
          var pos = parseInt(link.dataset.position, 10);
          if (qid && oid) {
            window.SlAlgoliaEvents.trackSearchClick(oid, qid, pos);
            sessionStorage.setItem('sl_algolia_query_id', qid);
          }
        }
      } catch(err) { /* silent */ }
    });

    /* ── Search logic ── */

    var doSearch = debounce(function() {
      var query = searchInput.value.trim();

      /* Show/hide clear button */
      clearBtn.hidden = !query;

      if (!query) {
        resultsContainer.innerHTML = '';
        lastProductsTotal = 0;
        return;
      }

      /* Build multi-index search request */
      var queries = [
        { indexName: suggestionsIndex, query: query, params: { hitsPerPage: 5 } }
      ];
      if (query.length >= 3) {
        queries.push({
          indexName: productsIndex,
          query: query,
          params: {
            /* Hex configurator components/parent carry tags:do-not-display
               (spec 2.2) so they never surface in mobile search suggestions. */
            filters: 'NOT tags:"do-not-display"',
            hitsPerPage: 4,
            clickAnalytics: true,
            enablePersonalization: true,
            userToken: config.userToken
          }
        });
      }

      client.search(queries).then(function(res) {
        var suggestionsRes = res.results[0];
        var productsRes = queries.length > 1 ? res.results[1] : null;

        if (productsRes) {
          lastQueryID = productsRes.queryID || '';
          lastProductsTotal = productsRes.nbHits || 0;
        }

        renderResults(query, suggestionsRes.hits, productsRes ? productsRes.hits : []);
      }).catch(function() {
        resultsContainer.innerHTML = '<p class="sl-mobile-empty">Something went wrong. Try again.</p>';
      });
    }, 200);

    searchInput.addEventListener('input', doSearch);

    /* ── Render results ── */

    function renderResults(query, suggestions, products) {
      var html = '';

      /* Suggestions */
      if (suggestions.length > 0) {
        html += '<div class="sl-mobile-search-heading">Suggestions</div>';
        for (var s = 0; s < suggestions.length; s++) {
          var sg = suggestions[s];
          html +=
            '<a class="sl-mobile-suggestion" href="/search?q=' + encodeURIComponent(sg.query) + '&type=product" data-suggestion-query="' + escAttr(sg.query) + '" aria-label="Search for ' + escAttr(sg.query) + '">' +
              '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9aacb8" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>' +
              '<span>' + esc(sg.query) + '</span>' +
            '</a>';
        }
      }

      /* Products */
      if (products.length > 0) {
        html += '<div class="sl-mobile-search-heading">Products</div>';
        for (var p = 0; p < products.length; p++) {
          html += renderProductCard(products[p], p);
        }

        /* View all link */
        if (lastProductsTotal > 0) {
          html +=
            '<a class="sl-mobile-viewall" href="/search?q=' + encodeURIComponent(query) + '&type=product" aria-label="View all ' + lastProductsTotal + ' results for ' + escAttr(query) + '">' +
              'View all ' + lastProductsTotal + ' results \u2192' +
            '</a>';
        }
      } else if (query.length >= 3) {
        html += '<p class="sl-mobile-empty">No products found for \u201C' + esc(query) + '\u201D</p>';
      }

      resultsContainer.innerHTML = html;

      /* Announce results to screen readers */
      if (lastProductsTotal > 0 && query) {
        srAnnounce.textContent = lastProductsTotal + ' products found for ' + query;
      } else if (query.length >= 3 && products.length === 0) {
        srAnnounce.textContent = 'No products found for ' + query;
      }

      /* Suggestion tap: fill input and re-search */
      resultsContainer.querySelectorAll('.sl-mobile-suggestion').forEach(function(link) {
        link.addEventListener('click', function(e) {
          e.preventDefault();
          var q = link.dataset.suggestionQuery;
          if (q) {
            searchInput.value = q;
            clearBtn.hidden = false;
            doSearch();
          }
        });
      });

      /* Trigger Reviews.io rating snippets */
      if (typeof ratingSnippet === 'function') {
        try {
          ratingSnippet('ruk_rating_snippet', {
            store: 'simple-lighting',
            color: '#F5A523',
            linebreak: false,
            text: 'Reviews',
            showEmptyStars: true
          });
        } catch(e) { /* Reviews.io not loaded — OK */ }

        /* Hide 0-review ratings */
        setTimeout(function() {
          resultsContainer.querySelectorAll('.ruk_rating_snippet').forEach(function(el) {
            var text = el.textContent || '';
            if (text.indexOf('(0 Reviews)') !== -1 || text.indexOf('(0)') !== -1 || text.trim() === '') {
              el.style.display = 'none';
            }
          });
        }, 600);
      }
    }

    function renderProductCard(hit, idx) {
      var url = '/products/' + (hit.handle || '');
      var img = hit.product_image || hit.image || '';
      var title = hit.title || '';
      var price = getCheapestPrice(hit);
      var priceInPence = Math.round(parseFloat(price) * 100) || 0;

      /* SKU for Reviews.io */
      var parentSku = (hit.meta && hit.meta.custom && hit.meta.custom.parent_sku) ? hit.meta.custom.parent_sku : '';
      var allVariantSkus = (hit.meta && hit.meta.custom && hit.meta.custom.all_variant_skus) ? hit.meta.custom.all_variant_skus : (hit.sku || '');
      var sku = parentSku ? parentSku + ';' + allVariantSkus : allVariantSkus;

      var card =
        '<a class="sl-mobile-product" href="' + escAttr(url) + '" ' +
          'aria-label="' + escAttr(title) + ', ' + fmtPrice(price) + '" ' +
          'data-object-id="' + escAttr(hit.objectID) + '" ' +
          'data-query-id="' + escAttr(lastQueryID) + '" ' +
          'data-position="' + (idx + 1) + '">';

      /* Image */
      if (img) {
        /* 80×80 thumbnail — request 80px-wide image from Shopify CDN (preserves any existing ?v= cache-buster). */
        var imgSized = img + (img.indexOf('?') !== -1 ? '&' : '?') + 'width=80';
        card += '<img class="sl-mobile-product-img" src="' + escAttr(imgSized) + '" alt="' + escAttr(title) + '" width="80" height="80" loading="lazy">';
      } else {
        card += '<div class="sl-mobile-product-img sl-mobile-product-img--placeholder"></div>';
      }

      /* Info */
      card += '<div class="sl-mobile-product-info">';
      card += '<div class="sl-mobile-product-title">' + esc(title) + '</div>';

      /* Reviews.io snippet */
      if (sku) {
        card += '<div class="ruk_rating_snippet" data-sku="' + escAttr(sku) + '" style="min-height:16px;"></div>';
      }

      /* Price + VAT */
      card += '<div class="sl-mobile-product-price-row">';
      card += '<span class="sl-mobile-product-price" data-price-inc="' + priceInPence + '">' + fmtPrice(price) + '</span>';
      card += '<span class="sl-mobile-product-vat">' + vatLabel() + '</span>';
      card += '</div>';

      card += '</div></a>';
      return card;
    }
  }

  /* ── Boot ── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { waitForDeps(init); });
  } else {
    waitForDeps(init);
  }
})();









